import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import article from "./TaxifyContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";
import './PagesCss/StockProfitCalculator.css';

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
);

const DividendTaxCalculator = () => {
  const content = article.find((item) => item.id === 3);
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const [inputs, setInputs] = useState({
    stockName: "",
    sharesOwned: "100",
    dividendPerShare: "2.50",
    dividendFrequency: "quarterly",
    dividendTaxRate: "15",
    otherDeductions: "0",
    dividendGrowth: "5",
    holdingPeriod: "10",
    reinvestDividends: "true",
    currencyConversion: "1",
    country: "us",
    taxYear: "2024",
    incomeBracket: "middle",
    dividendType: "qualified",
    additionalTax: "0",
    inflationRate: "3",
    dividendYield: "2.5",
    stockPrice: "100.00",
    portfolioValue: "10000",
    taxCredit: "0",
    foreignTax: "0"
  });

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);
  const [dividendProjection, setDividendProjection] = useState([]);

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculateDividendTax();
    }
  }, [inputs, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setInputs(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked.toString() : value
    }));
  };

  const getTaxRates = (country, dividendType, incomeBracket) => {
    const taxRates = {
      us: {
        qualified: {
          low: 0.00,
          middle: 0.15,
          high: 0.20,
          veryHigh: 0.23
        },
        nonQualified: {
          low: 0.10,
          middle: 0.22,
          high: 0.35,
          veryHigh: 0.37
        },
        reit: {
          low: 0.10,
          middle: 0.22,
          high: 0.35,
          veryHigh: 0.37
        }
      },
      uk: {
        qualified: {
          low: 0.00,
          middle: 0.075,
          high: 0.325,
          veryHigh: 0.381
        },
        nonQualified: {
          low: 0.075,
          middle: 0.325,
          high: 0.381,
          veryHigh: 0.426
        }
      },
      canada: {
        qualified: {
          low: 0.00,
          middle: 0.15,
          high: 0.20,
          veryHigh: 0.33
        },
        nonQualified: {
          low: 0.15,
          middle: 0.20,
          high: 0.26,
          veryHigh: 0.33
        }
      },
      australia: {
        qualified: {
          low: 0.00,
          middle: 0.19,
          high: 0.325,
          veryHigh: 0.45
        },
        nonQualified: {
          low: 0.19,
          middle: 0.325,
          high: 0.37,
          veryHigh: 0.45
        }
      }
    };

    const rates = taxRates[country] || taxRates.us;
    return rates[dividendType]?.[incomeBracket] || rates.qualified.middle;
  };

  const calculateDividendTax = () => {
    const {
      stockName,
      sharesOwned,
      dividendPerShare,
      dividendFrequency,
      dividendTaxRate,
      otherDeductions,
      dividendGrowth,
      holdingPeriod,
      reinvestDividends,
      currencyConversion,
      country,
      taxYear,
      incomeBracket,
      dividendType,
      additionalTax,
      inflationRate,
      dividendYield,
      stockPrice,
      portfolioValue,
      taxCredit,
      foreignTax
    } = inputs;

    if (!sharesOwned || !dividendPerShare) {
      return;
    }

    const shares = parseFloat(sharesOwned);
    const dividend = parseFloat(dividendPerShare);
    const customTaxRate = parseFloat(dividendTaxRate || 0);
    const deductions = parseFloat(otherDeductions || 0);
    const growth = parseFloat(dividendGrowth || 0) / 100;
    const period = parseFloat(holdingPeriod || 1);
    const curr = parseFloat(currencyConversion || 1);
    const addTax = parseFloat(additionalTax || 0);
    const inflation = parseFloat(inflationRate || 0) / 100;
    const yieldRate = parseFloat(dividendYield || 0) / 100;
    const price = parseFloat(stockPrice || 0);
    const portfolio = parseFloat(portfolioValue || 0);
    const credit = parseFloat(taxCredit || 0);
    const foreign = parseFloat(foreignTax || 0);

    // Calculate tax rate
    const autoTaxRate = getTaxRates(country, dividendType, incomeBracket);
    const effectiveTaxRate = customTaxRate > 0 ? customTaxRate / 100 : autoTaxRate;

    // Adjust dividend based on frequency
    let annualDividend = dividend;
    let paymentsPerYear = 1;
    switch(dividendFrequency) {
      case "monthly": 
        annualDividend *= 12;
        paymentsPerYear = 12;
        break;
      case "quarterly": 
        annualDividend *= 4;
        paymentsPerYear = 4;
        break;
      case "semiAnnual": 
        annualDividend *= 2;
        paymentsPerYear = 2;
        break;
      default: 
        paymentsPerYear = 1;
    }

    // Calculate initial values
    const initialGrossIncome = shares * annualDividend;
    const portfolioYield = price > 0 ? (annualDividend / price) * 100 : yieldRate;

    // Project dividends over holding period
    const projection = [];
    let totalGrossIncome = 0;
    let totalTaxPaid = 0;
    let currentShares = shares;
    let currentDividend = annualDividend;

    for (let year = 1; year <= period; year++) {
      // Calculate annual dividend for current year
      const yearDividend = currentShares * currentDividend;
      const yearTax = yearDividend * effectiveTaxRate + addTax - Math.min(credit, yearDividend * effectiveTaxRate);
      const yearNet = yearDividend - yearTax - foreign;
      
      totalGrossIncome += yearDividend;
      totalTaxPaid += yearTax;

      projection.push({
        year,
        grossIncome: yearDividend,
        taxAmount: yearTax,
        netIncome: yearNet,
        shares: currentShares,
        dividendPerShare: currentDividend,
        cumulativeNet: totalGrossIncome - totalTaxPaid
      });

      // Apply growth and reinvestment
      currentDividend *= (1 + growth);
      if (reinvestDividends === "true") {
        const sharesPurchased = yearNet / price;
        currentShares += sharesPurchased;
      }
    }

    // Calculate metrics
    const averageTaxRate = totalGrossIncome > 0 ? (totalTaxPaid / totalGrossIncome) * 100 : 0;
    const taxEfficiency = ((totalGrossIncome - totalTaxPaid) / totalGrossIncome) * 100;
    const annualizedReturn = (Math.pow((totalGrossIncome - totalTaxPaid) / (shares * price), 1/period) - 1) * 100;
    
    // Inflation-adjusted values
    const inflationAdjustedNet = (totalGrossIncome - totalTaxPaid) / Math.pow(1 + inflation, period);

    // Portfolio metrics
    const portfolioIncome = (portfolio * portfolioYield / 100);
    const portfolioTax = portfolioIncome * effectiveTaxRate;
    const portfolioNet = portfolioIncome - portfolioTax;

    setResult({
      stockName: stockName || "Your Stock",
      totalGrossIncome: totalGrossIncome * curr,
      totalTaxAmount: totalTaxPaid * curr,
      totalNetIncome: (totalGrossIncome - totalTaxPaid) * curr,
      averageTaxRate: averageTaxRate,
      effectiveTaxRate: effectiveTaxRate * 100,
      taxEfficiency: taxEfficiency,
      annualizedReturn: annualizedReturn,
      finalShares: currentShares,
      portfolioYield: portfolioYield,
      portfolioIncome: portfolioIncome * curr,
      portfolioTax: portfolioTax * curr,
      portfolioNet: portfolioNet * curr,
      inflationAdjustedNet: inflationAdjustedNet * curr,
      holdingPeriod: period,
      dividendType: dividendType,
      country: country
    });

    setDividendProjection(projection);
  };

  const calculateTaxOptimization = () => {
    const { country, dividendType, incomeBracket, portfolioValue, dividendYield } = inputs;
    
    if (!portfolioValue || !dividendYield) return null;

    const portfolio = parseFloat(portfolioValue);
    const yieldRate = parseFloat(dividendYield) / 100;

    const strategies = [];

    // Tax-advantaged accounts
    strategies.push({
      strategy: "Tax-Advantaged Accounts",
      description: "Hold dividend stocks in IRA/401(k) accounts",
      potentialSavings: portfolio * yieldRate * 0.15,
      complexity: "Medium",
      accountType: "Retirement"
    });

    // Qualified dividends
    if (dividendType === "nonQualified") {
      strategies.push({
        strategy: "Qualified Dividend Focus",
        description: "Focus on stocks with qualified dividend treatment",
        potentialSavings: portfolio * yieldRate * 0.07,
        complexity: "Low",
        accountType: "Taxable"
      });
    }

    // Tax-loss harvesting
    strategies.push({
      strategy: "Tax-Loss Harvesting",
      description: "Offset dividend income with capital losses",
      potentialSavings: portfolio * yieldRate * 0.05,
      complexity: "High",
      accountType: "Taxable"
    });

    // Foreign tax credit optimization
    strategies.push({
      strategy: "Foreign Tax Credit",
      description: "Optimize foreign tax credit utilization",
      potentialSavings: portfolio * yieldRate * 0.03,
      complexity: "High",
      accountType: "Taxable"
    });

    return strategies;
  };

  const resetCalculator = () => {
    setInputs({
      stockName: "",
      sharesOwned: "100",
      dividendPerShare: "2.50",
      dividendFrequency: "quarterly",
      dividendTaxRate: "15",
      otherDeductions: "0",
      dividendGrowth: "5",
      holdingPeriod: "10",
      reinvestDividends: "true",
      currencyConversion: "1",
      country: "us",
      taxYear: "2024",
      incomeBracket: "middle",
      dividendType: "qualified",
      additionalTax: "0",
      inflationRate: "3",
      dividendYield: "2.5",
      stockPrice: "100.00",
      portfolioValue: "10000",
      taxCredit: "0",
      foreignTax: "0"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Metric,Value",
      `Stock Name,${result.stockName}`,
      `Total Gross Income,${result.totalGrossIncome.toFixed(2)}`,
      `Total Tax Amount,${result.totalTaxAmount.toFixed(2)}`,
      `Total Net Income,${result.totalNetIncome.toFixed(2)}`,
      `Average Tax Rate,${result.averageTaxRate.toFixed(2)}%`,
      `Effective Tax Rate,${result.effectiveTaxRate.toFixed(2)}%`,
      `Tax Efficiency,${result.taxEfficiency.toFixed(2)}%`,
      `Annualized Return,${result.annualizedReturn.toFixed(2)}%`,
      `Final Shares,${result.finalShares.toFixed(2)}`,
      `Portfolio Yield,${result.portfolioYield.toFixed(2)}%`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dividend-tax-calculation.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      result: { ...result }
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setResult(calc.result);
    setShowSaved(false);
    addNotification("Calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      usQualified: { 
        country: "us",
        dividendType: "qualified",
        incomeBracket: "middle"
      },
      usNonQualified: { 
        country: "us",
        dividendType: "nonQualified", 
        incomeBracket: "middle"
      },
      taxFree: {
        country: "us",
        dividendType: "qualified",
        incomeBracket: "low"
      }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset} dividend preset applied`, "info");
  };

  // Chart Data
  const incomeChartData = result ? {
    labels: ["Net Dividend Income", "Dividend Tax", "Gross Dividend Income"],
    datasets: [{
      data: [
        Math.max(result.totalNetIncome, 0),
        Math.max(result.totalTaxAmount, 0),
        Math.max(result.totalGrossIncome, 0)
      ],
      backgroundColor: ["#28A745", "#FF6B6B", "#4ECDC4"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  const projectionChartData = dividendProjection.length > 0 ? {
    labels: dividendProjection.map(p => `Year ${p.year}`),
    datasets: [
      {
        label: 'Gross Income',
        data: dividendProjection.map(p => p.grossIncome),
        borderColor: "#4ECDC4",
        backgroundColor: "rgba(78, 205, 196, 0.1)",
        fill: true,
        tension: 0.4
      },
      {
        label: 'Net Income',
        data: dividendProjection.map(p => p.netIncome),
        borderColor: "#28A745",
        backgroundColor: "rgba(40, 167, 69, 0.1)",
        fill: true,
        tension: 0.4
      },
      {
        label: 'Tax Paid',
        data: dividendProjection.map(p => p.taxAmount),
        borderColor: "#FF6B6B",
        backgroundColor: "rgba(255, 107, 107, 0.1)",
        fill: true,
        tension: 0.4
      }
    ]
  } : null;

  const optimizationStrategies = calculateTaxOptimization();

  return (
    <div className="profit-calculator">
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Dividend Tax Calculator</h1>
        </div>
        <p>Advanced dividend income analysis with multi-country tax optimization and growth projections</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">{calc.result.stockName}</div>
                    <div className="saved-details">
                      <span className="profit">
                        ${calc.result.totalNetIncome.toFixed(2)}
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Tax Calculator'}
              </button>
              <button 
                className={`tab ${activeTab === "projection" ? "active" : ""}`}
                onClick={() => setActiveTab("projection")}
              >
                <i className="fas fa-chart-line"></i>
                {isMobile ? 'Projection' : 'Income Projection'}
              </button>
              <button 
                className={`tab ${activeTab === "optimization" ? "active" : ""}`}
                onClick={() => setActiveTab("optimization")}
              >
                <i className="fas fa-lightbulb"></i>
                {isMobile ? 'Optimize' : 'Tax Optimization'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Tax Presets:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('usQualified')}>
                US Qualified
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('usNonQualified')}>
                US Non-Qualified
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('taxFree')}>
                Tax-Free
              </button>
            </div>
          </div>

          {/* Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-chart-line"></i>
                    Dividend Details
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-signature"></i> Stock Name</label>
                      <input 
                        type="text" 
                        name="stockName" 
                        value={inputs.stockName} 
                        onChange={handleChange}
                        placeholder="Stock / ETF Name"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-layer-group"></i> Shares Owned</label>
                      <input 
                        type="number" 
                        name="sharesOwned" 
                        value={inputs.sharesOwned} 
                        onChange={handleChange}
                        placeholder="100"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Dividend per Share</label>
                      <input 
                        type="number" 
                        name="dividendPerShare" 
                        value={inputs.dividendPerShare} 
                        onChange={handleChange}
                        placeholder="2.50"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar"></i> Dividend Frequency</label>
                      <select name="dividendFrequency" value={inputs.dividendFrequency} onChange={handleChange}>
                        <option value="yearly">Yearly</option>
                        <option value="semiAnnual">Semi-Annual</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Dividend Growth (%)</label>
                      <input 
                        type="number" 
                        name="dividendGrowth" 
                        value={inputs.dividendGrowth} 
                        onChange={handleChange}
                        placeholder="5"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar-alt"></i> Holding Period (Years)</label>
                      <input 
                        type="number" 
                        name="holdingPeriod" 
                        value={inputs.holdingPeriod} 
                        onChange={handleChange}
                        placeholder="10"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-sliders-h"></i>
                    Tax Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-flag"></i> Country</label>
                      <select name="country" value={inputs.country} onChange={handleChange}>
                        <option value="us">United States</option>
                        <option value="uk">United Kingdom</option>
                        <option value="canada">Canada</option>
                        <option value="australia">Australia</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-file-invoice-dollar"></i> Dividend Type</label>
                      <select name="dividendType" value={inputs.dividendType} onChange={handleChange}>
                        <option value="qualified">Qualified</option>
                        <option value="nonQualified">Non-Qualified</option>
                        <option value="reit">REIT</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-money-bill-wave"></i> Income Bracket</label>
                      <select name="incomeBracket" value={inputs.incomeBracket} onChange={handleChange}>
                        <option value="low">Low (0-15%)</option>
                        <option value="middle">Middle (22-25%)</option>
                        <option value="high">High (35-40%)</option>
                        <option value="veryHigh">Very High (45%+)</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Custom Tax Rate (%)</label>
                      <input 
                        type="number" 
                        name="dividendTaxRate" 
                        value={inputs.dividendTaxRate} 
                        onChange={handleChange}
                        placeholder="15"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-hand-holding-usd"></i> Additional Tax</label>
                      <input 
                        type="number" 
                        name="additionalTax" 
                        value={inputs.additionalTax} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrows-repeat"></i> Currency Rate</label>
                      <input 
                        type="number" 
                        name="currencyConversion" 
                        value={inputs.currencyConversion} 
                        onChange={handleChange}
                        placeholder="1.00"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Income Projection Tab */}
          {activeTab === "projection" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-cogs"></i>
                  Advanced Settings
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-chart-line"></i> Dividend Yield (%)</label>
                    <input 
                      type="number" 
                      name="dividendYield" 
                      value={inputs.dividendYield} 
                      onChange={handleChange}
                      placeholder="2.5"
                      step="0.1"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-tag"></i> Stock Price</label>
                    <input 
                      type="number" 
                      name="stockPrice" 
                      value={inputs.stockPrice} 
                      onChange={handleChange}
                      placeholder="100.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-piggy-bank"></i> Portfolio Value</label>
                    <input 
                      type="number" 
                      name="portfolioValue" 
                      value={inputs.portfolioValue} 
                      onChange={handleChange}
                      placeholder="10000"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-percentage"></i> Inflation Rate (%)</label>
                    <input 
                      type="number" 
                      name="inflationRate" 
                      value={inputs.inflationRate} 
                      onChange={handleChange}
                      placeholder="3"
                      step="0.1"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-award"></i> Tax Credit</label>
                    <input 
                      type="number" 
                      name="taxCredit" 
                      value={inputs.taxCredit} 
                      onChange={handleChange}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-globe"></i> Foreign Tax Paid</label>
                    <input 
                      type="number" 
                      name="foreignTax" 
                      value={inputs.foreignTax} 
                      onChange={handleChange}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-receipt"></i> Other Deductions</label>
                    <input 
                      type="number" 
                      name="otherDeductions" 
                      value={inputs.otherDeductions} 
                      onChange={handleChange}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                </div><br/>

                <div className="input-row">
                    <label>
                      <input 
                        type="checkbox" 
                        name="reinvestDividends" 
                        checked={inputs.reinvestDividends === "true"} 
                        onChange={handleChange}
                      />
                      <i className="fa-solid fa-arrows-rotate"></i> Reinvest Dividends
                    </label>
                </div><br/>

                {dividendProjection.length > 0 && (
                  <div className="position-breakdown">
                    <h4>Projection Summary</h4>
                    <div className="projection-stats">
                      <div className="breakdown-item">
                        <span>Final Shares:</span>
                        <span>{dividendProjection[dividendProjection.length - 1]?.shares.toFixed(2)}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Final DPS:</span>
                        <span>${dividendProjection[dividendProjection.length - 1]?.dividendPerShare.toFixed(2)}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Cumulative Net:</span>
                        <span>${dividendProjection[dividendProjection.length - 1]?.cumulativeNet.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tax Optimization Tab */}
          {activeTab === "optimization" && optimizationStrategies && (
            <div className="risk-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-lightbulb"></i>
                  Tax Optimization Strategies
                </h3>
                
                <div className="position-breakdown">
                  {optimizationStrategies.map((strategy, index) => (
                    <div key={index} className="optimization-item">
                      <div className="breakdown-item">
                        <span className="optimization-name">{strategy.strategy}</span>
                        <span className="optimization-savings positive">
                          Save ${strategy.potentialSavings.toFixed(2)}
                        </span>
                      </div>
                      <div className="optimization-details">
                        <span>{strategy.description}</span>
                        <div className="breakdown-item">
                          <span className={`complexity ${strategy.complexity.toLowerCase()}`}>
                            {strategy.complexity}
                          </span>
                          <span className="account-type">
                            {strategy.accountType}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className="history-profit">
                          <i className="fas fa-dollar-sign"></i>
                          ${calc.result.totalNetIncome.toFixed(2)}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-percentage"></i> Tax: {calc.result.averageTaxRate.toFixed(2)}%</span>
                        <span><i className="fas fa-chart-line"></i> Return: {calc.result.annualizedReturn.toFixed(2)}%</span>
                        <span><i className="fas fa-flag"></i> {calc.result.country.toUpperCase()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "projection" || activeTab === "optimization") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-file-invoice-dollar"></i>
                    Dividend Tax Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value positive">
                          ${result.totalNetIncome.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-hand-holding-usd"></i>
                          Total Net Income
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${result.totalGrossIncome.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-chart-line"></i>
                          Gross Dividend Income
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value negative">
                          ${result.totalTaxAmount.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-file-invoice-dollar"></i>
                          Total Tax Paid
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.annualizedReturn.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-chart-line"></i>
                          Annualized Return
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-percentage"></i> Effective Tax Rate:</span>
                        <span>{result.effectiveTaxRate.toFixed(2)}%</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-percentage"></i> Average Tax Rate:</span>
                        <span>{result.averageTaxRate.toFixed(2)}%</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-chart-line"></i> Tax Efficiency:</span>
                        <span>{result.taxEfficiency.toFixed(2)}%</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-layer-group"></i> Final Shares:</span>
                        <span>{result.finalShares.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-percentage"></i> Portfolio Yield:</span>
                        <span>{result.portfolioYield.toFixed(2)}%</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-dollar-sign"></i> Inflation-Adjusted Net:</span>
                        <span>${result.inflationAdjustedNet.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Tax Efficiency */}
                    <div className="risk-summary">
                      <h4>Tax Efficiency Analysis</h4>
                      <div className="risk-cards">
                        <div className="risk-card">
                          <div className={`risk-value ${result.taxEfficiency > 85 ? 'positive' : result.taxEfficiency > 70 ? 'moderate' : 'negative'}`}>
                            {result.taxEfficiency > 85 ? 'Excellent' : result.taxEfficiency > 70 ? 'Good' : 'Poor'}
                          </div>
                          <div className="risk-label">Tax Efficiency</div>
                        </div>
                        <div className="risk-card">
                          <div className={`risk-value ${result.effectiveTaxRate < 15 ? 'positive' : result.effectiveTaxRate < 25 ? 'moderate' : 'negative'}`}>
                            {result.effectiveTaxRate < 15 ? 'Low' : result.effectiveTaxRate < 25 ? 'Medium' : 'High'}
                          </div>
                          <div className="risk-label">Tax Burden</div>
                        </div>
                      </div>
                    </div>

                    {/* Chart - FIXED TOOLTIP */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Income Distribution
                      </h3>
                      <Doughnut 
                        data={incomeChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  const value = context.parsed;
                                  if (typeof value === 'number' && !isNaN(value)) {
                                    return `$${value.toFixed(2)}`;
                                  }
                                  return `$${0}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-file-invoice-dollar fa-3x"></i>
                    <p>Enter your dividend details to calculate tax impact</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "projection" && dividendProjection.length > 0 && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-chart-line"></i>
                  Dividend Income Projection
                </h3>
                
                <div className="chart-container">
                  <Line 
                    data={projectionChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: true,
                          position: 'top'
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            callback: function(value) {
                              return '$' + value.toLocaleString();
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>

                <div className="projection-summary">
                  <div className="summary-card">
                    <div className="summary-value positive">
                      ${dividendProjection[dividendProjection.length - 1]?.cumulativeNet.toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-hand-holding-usd"></i>
                      Cumulative Net Income
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {result.annualizedReturn.toFixed(2)}%
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-chart-line"></i>
                      Annualized Return
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {result.finalShares.toFixed(0)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-layer-group"></i>
                      Final Shares
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      ${dividendProjection[dividendProjection.length - 1]?.dividendPerShare.toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-dollar-sign"></i>
                      Final DPS
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "optimization" && optimizationStrategies && (
              <div className="risk-results-panel">
                <h3>
                  <i className="fas fa-lightbulb"></i>
                  Tax Optimization Summary
                </h3><br/>
                
                <div className="optimization-summary">
                  <div className="summary-card">
                    <div className="summary-value positive">
                      ${optimizationStrategies.reduce((sum, strategy) => sum + strategy.potentialSavings, 0).toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-dollar-sign"></i>
                      Total Potential Savings
                    </div>
                  </div><br/>
                  <div className="summary-card">
                    <div className="summary-value">
                      {optimizationStrategies.filter(s => s.complexity === 'Low').length}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-bolt"></i>
                      Quick Wins
                    </div>
                  </div><br/>
                  <div className="summary-card">
                    <div className="summary-value">
                      {optimizationStrategies.filter(s => s.accountType === 'Retirement').length}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-piggy-bank"></i>
                      Retirement Strategies
                    </div>
                  </div>
                </div><br/>

                <div className="position-breakdown">
                  <h4>Implementation Priority</h4>
                  {optimizationStrategies
                    .sort((a, b) => {
                      const complexityOrder = { 'Low': 1, 'Medium': 2, 'High': 3 };
                      return complexityOrder[a.complexity] - complexityOrder[b.complexity];
                    })
                    .map((strategy, index) => (
                      <div key={index} className="breakdown-item">
                        <span>{strategy.strategy}:</span>
                        <span className={`priority ${strategy.complexity.toLowerCase()}`}>
                          {strategy.accountType}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default DividendTaxCalculator;